import { selectCurrUser } from '@/store/bootstrap/selectors';
import { Avatar, Flex, Text, Link } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';

const recordTypeMap = {
  AtsInterview: 'Interview',
  AtsReview: 'Review',
  AtsAssessment: 'Assessment',
};

export const ScorecardHeader = ({
  scoreCardDetails = [],
  showOnlyCurrUser = false,
  isMobile = false,
  editButton,
  aiButton,
  applicationId = null,
  jobId = null,
  showCandidateLink = false,
}) => {
  const currUser = useSelector(selectCurrUser);
  const currScoreCard = showOnlyCurrUser
    ? scoreCardDetails?.find((item) => item?.reviewer?.id === currUser?.id)
    : scoreCardDetails?.[0];

  const candidate = currScoreCard?.candidate;
  const dataName =
    currScoreCard?.record?.substituted_name || currScoreCard?.record?.name;
  const reviewer = currScoreCard?.reviewer;
  const type =
    recordTypeMap?.[currScoreCard?.record_type] || recordTypeMap?.AtsInterview;

  const isSingleScorecard = scoreCardDetails?.length === 1 || showOnlyCurrUser;

  const reviewerName = () => {
    if (!isSingleScorecard) return null;
    return (
      <Flex
        flexDir="column"
        gap="8px"
        w={{ base: '100%', md: 'auto' }}
        minW="100px"
        flex={{ base: '1 1 auto', md: '1 1 auto' }}
        overflow="hidden"
      >
        <Text size="sm-semi-bold">{`${type}er`}</Text>
        <Flex gap="6px" alignItems="center" minW={0} flex={1}>
          <Avatar
            w="16px"
            h="16px"
            borderRadius="xs"
            src={reviewer?.avatar}
            name={reviewer?.name}
            className="avatar-xs"
            flexShrink={0}
          />
          <TruncatedTextWithTooltip
            data-test-id="single-scorecard-reviewer-name"
            size="sm-medium"
            text={reviewer?.name}
            enableTooltip={true}
          />
        </Flex>
      </Flex>
    );
  };

  const candidateName = () => {
    const shouldShowLink = showCandidateLink && applicationId && jobId;
    const candidateContent = (
      <Flex gap="6px" alignItems="center" minW={0} flex={1}>
        <Avatar
          w="16px"
          h="16px"
          borderRadius="xs"
          src={candidate?.avatar}
          name={candidate?.name}
          className="avatar-xs"
          flexShrink={0}
        />
        <TruncatedTextWithTooltip
          size="sm-medium"
          data-test-id="scorecard-candidate-name"
          text={candidate?.name}
          enableTooltip={true}
          color={shouldShowLink ? 'ats-primary' : undefined}
        />
      </Flex>
    );

    return (
      <Flex
        flexDir="column"
        gap="8px"
        minW="100px"
        flex={{ base: '1 1 auto', md: '1 1 auto' }}
        overflow="hidden"
      >
        <Text size="sm-semi-bold">Candidate</Text>
        {shouldShowLink ? (
          <Link
            href={`/jobs/${jobId}?tab=candidates&appId=${applicationId}`}
            _hover={{ textDecoration: 'none' }}
            _focus={{ boxShadow: 'none' }}
            target="_blank"
          >
            {candidateContent}
          </Link>
        ) : (
          candidateContent
        )}
      </Flex>
    );
  };

  const interviewerName = () => {
    return (
      <Flex
        flexDir="column"
        gap="8px"
        minW="100px"
        flex={{ base: '1 1 auto', md: '1 1 auto' }}
        overflow="hidden"
      >
        <Text size="sm-semi-bold" textTransform="capitalize">
          {type}
        </Text>

        <TruncatedTextWithTooltip
          size="sm-medium"
          data-test-id="scorecard-activity-name"
          text={dataName}
          enableTooltip={true}
        />
      </Flex>
    );
  };

  const scorecardBanner = () => {
    if (!isSingleScorecard) return null;

    if (aiButton || editButton)
      return (
        <Flex
          bg="ats-tertiary"
          position="relative"
          px={isMobile ? '18px' : '16px'}
          py="8px"
          alignItems="center"
          justifyContent="space-between"
          gap="12px"
        >
          <Flex gap="12px">
            {aiButton?.()}
            {editButton?.()}
          </Flex>
        </Flex>
      );
  };

  return (
    <>
      <Flex p="10px 9px 12px 9px" w="100%" maxW="100%">
        <Flex
          w="100%"
          p="12px"
          gap="24px"
          justifyContent="flex-start"
          flexDir={{ base: 'column', md: 'row' }}
          alignItems={{ base: 'flex-start', md: 'center' }}
          bg="secondary-25"
          borderRadius="sm"
        >
          {reviewerName()}
          {candidateName()}
          {interviewerName()}
        </Flex>
      </Flex>
      {scorecardBanner()}
    </>
  );
};
